import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export function SignaturePad({ onSaved, testid = "signature" }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0B1530";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const down = (e) => {
    e.preventDefault();
    ref.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...pos(e));
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(...pos(e));
    ctx.stroke();
    setDirty(true);
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setDirty(false);
    setSaved(null);
    onSaved(null);
  };
  const save = () => {
    ref.current.toBlob(async (blob) => {
      const fd = new FormData();
      fd.append("files", new File([blob], "recipient-signature.png", { type: "image/png" }));
      try {
        const { data } = await api.post("/attachments", fd);
        setSaved(data[0]);
        onSaved(data[0]);
        toast.success("Signature captured");
      } catch (e) { toast.error(errMsg(e)); }
    }, "image/png");
  };

  return (
    <div className="space-y-2">
      <canvas ref={ref} data-testid={`${testid}-canvas`}
        className={`w-full h-40 rounded-md border-2 border-dashed bg-white touch-none ${saved ? "border-emerald-500" : "border-input"}`}
        onPointerDown={down} onPointerMove={move} onPointerUp={() => { drawing.current = false; }} onPointerLeave={() => { drawing.current = false; }} />
      <div className="flex gap-2 items-center">
        <Button type="button" variant="outline" size="sm" onClick={clear} data-testid={`${testid}-clear-btn`}><Eraser className="h-4 w-4 mr-1" />Clear</Button>
        <Button type="button" size="sm" disabled={!dirty || !!saved} onClick={save} data-testid={`${testid}-save-btn`}><Check className="h-4 w-4 mr-1" />{saved ? "Saved" : "Use this signature"}</Button>
        <span className="text-xs text-muted-foreground">Recipient signs with finger or stylus</span>
      </div>
    </div>
  );
}
